import { Box, Flex } from "@chakra-ui/layout";
import PageTitle from "../components/PageTitle";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
	Card,
	CardBody,
	CardHeader,
	Text,
} from "@chakra-ui/react";
import { ChevronRightIcon, DownloadIcon } from "@chakra-ui/icons";
import { NavLink, useParams } from "react-router-dom";
import { usePurchaseOrder } from "../hooks/purchase_order.hooks";
import ClientSideTable from "../components/table/ClientSideTable";
import {
	ColumnDef,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { format } from "date-fns";
import { Command } from "../types/command.types";
import PrinterIcon from "../components/icons/PrinterIcon";
import Tooltip from "../components/Tooltip";
import { useMutation } from "react-query";
import { fetchFileContent } from "../mutations/file.mutations";
import UPSTrackingSteps from "../components/UPSTrackingSteps";
import CardTitle from "../components/CardTitle";

const OrdersDeliveryNoteDetailPage = () => {
	const { id } = useParams();
	const { data: purchaseOrder } = usePurchaseOrder({ id });
	const { mutate: downloadFileContent } = useMutation(fetchFileContent, {
		onSuccess: (content: string) => {
			if (!purchaseOrder || !purchaseOrder.file) {
				return;
			}
			const blob = new Blob([content]);
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = purchaseOrder.file.fileName;
			link.download = purchaseOrder.file.createdAt
				? `BL${format(purchaseOrder.file?.createdAt, "yyyy")}${purchaseOrder.id}.pdf`
				: purchaseOrder.file.fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
	});
	const columns = useMemo<ColumnDef<Command>[]>(
		() => [
			{
				accessorFn: (row) => (
					<NavLink to={`/orders/${row.id}`}>
						<Text color="brand.700" textDecoration="underline">
							{row.exposedId}
						</Text>
					</NavLink>
				),
				id: "exposed_id",
				header: "ID",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => {
					if (row.patient) {
						return `${row.patient?.firstName} ${row.patient?.lastName}`;
					}
					return "N/A";
				},
				id: "patient",
				header: "Patient",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) =>
					row.patient
						? format(row.patient.birthDate, "yy/MM/yyyy")
						: "--/--/----",
				id: "birthdate",
				header: "Date de naissance",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	const table = useReactTable({
		data: purchaseOrder ? (purchaseOrder.commands ?? []) : [],
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});
	const displayedID = useMemo(() => {
		if (purchaseOrder === null || purchaseOrder === undefined) {
			return "";
		}
		if (purchaseOrder.createdAt === undefined) {
			return "";
		}
		return `BL${format(purchaseOrder.createdAt, "yyyy")}${purchaseOrder.id}`;
	}, [purchaseOrder]);

	if (purchaseOrder === null || purchaseOrder === undefined) {
		return;
	}
	return (
		<>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/orders/deliverynote"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Contact
					</BreadcrumbLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						<Text textTransform={"capitalize"}>{displayedID}</Text>
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<Flex justify="space-between" mb={4}>
				<Flex>
					<PageTitle>{displayedID}</PageTitle>
				</Flex>
				{purchaseOrder.fileId !== null && (
					<Flex gap={4}>
						<Button variant="outline">
							<PrinterIcon mr={4}></PrinterIcon>
							<Text>Imprimer</Text>
						</Button>
						<Button
							variant="outline"
							onClick={() => {
								if (purchaseOrder.file) {
									downloadFileContent(purchaseOrder.file.id);
								}
							}}
						>
							<Text mr={4}>Télécharger</Text>
							<DownloadIcon />
						</Button>
					</Flex>
				)}
				{purchaseOrder.fileId === null && (
					<Tooltip content="Ce bon de livraison n'est lié à aucun bon de commande">
						<Flex gap={4}>
							<Button variant="outline" isDisabled>
								<PrinterIcon mr={4}></PrinterIcon>
								<Text>Imprimer</Text>
							</Button>
							<Button variant="outline" isDisabled>
								<Text mr={4}>Télécharger</Text>
								<DownloadIcon />
							</Button>
						</Flex>
					</Tooltip>
				)}
			</Flex>

			<Flex direction="column" gap={4}>
				<Box w="min(1400px,100vw)">
					<Card>
						<CardHeader
							borderBottom="solid 1px"
							borderColor="zinc.50"
							lineHeight="27px"
							fontSize="18px"
							fontWeight="600"
							color="#101828"
						>
							Détails de la livraison
						</CardHeader>
						<CardBody>
							<Flex direction="column" gap={4}>
								<Flex
									justify="space-between"
									borderBottom="solid 1px"
									borderColor="#F1F5F9"
								>
									<Text
										lineHeight="21px"
										fontSize="14px"
										fontWeight="500"
										color="#344054"
									>
										ID
									</Text>
									<Text>{purchaseOrder.id}</Text>
								</Flex>

								<Flex
									justify="space-between"
									borderBottom="solid 1px"
									borderColor="#F1F5F9"
								>
									<Text
										lineHeight="21px"
										fontSize="14px"
										fontWeight="500"
										color="#344054"
									>
										Transporteur
									</Text>
									<Text>{purchaseOrder.deliveryType}</Text>
								</Flex>

								<Flex
									justify="space-between"
									borderBottom="solid 1px"
									borderColor="#F1F5F9"
								>
									<Text
										lineHeight="21px"
										fontSize="14px"
										fontWeight="500"
										color="#344054"
									>
										Preuve signature
									</Text>
									<Text></Text>
								</Flex>

								<Flex
									justify="space-between"
									borderBottom="solid 1px"
									borderColor="#F1F5F9"
								>
									<Text
										lineHeight="21px"
										fontSize="14px"
										fontWeight="500"
										color="#344054"
									>
										Heure de réception
									</Text>
									<Text></Text>
								</Flex>

								<Flex
									justify="space-between"
									borderBottom="solid 1px"
									borderColor="#F1F5F9"
								>
									<Text
										lineHeight="21px"
										fontSize="14px"
										fontWeight="500"
										color="#344054"
									>
										Numéro de suivi
									</Text>
									<Text>{purchaseOrder?.trackingId}</Text>
								</Flex>
							</Flex>
						</CardBody>
					</Card>
				</Box>

				<Box w="min(1400px,100vw)">
					<Card>
						<CardHeader
							borderBottom="solid 1px"
							borderColor="zinc.50"
							lineHeight="27px"
							fontSize="18px"
							fontWeight="600"
							color="#101828"
						>
							Commandes de la livraison
						</CardHeader>
						<CardBody>
							<ClientSideTable
								table={table}
								w={["auto", "240px", "200px", "300px", "10px"]}
								flexGrow={[1, 0, 0, 0, 0]}
								noSearch
								noPagination
							></ClientSideTable>
						</CardBody>
					</Card>
				</Box>

				{purchaseOrder?.trackingId && (
					<Card>
						<CardTitle>
							<Flex>
								<Box fontWeight={600} fontSize="18px">
									Activité lié à la livraison
								</Box>
							</Flex>
						</CardTitle>

						<CardBody>
							<UPSTrackingSteps id={purchaseOrder?.trackingId} />
						</CardBody>
					</Card>
				)}

				<Box w="min(1400px,100vw)">
					<Card>
						<CardHeader
							borderBottom="solid 1px"
							borderColor="zinc.50"
							lineHeight="27px"
							fontSize="18px"
							fontWeight="600"
							color="#101828"
						>
							Suivi de livraison
						</CardHeader>
						<CardBody>--</CardBody>
					</Card>
				</Box>
			</Flex>
		</>
	);
};

export default OrdersDeliveryNoteDetailPage;
